import React, { useState, useCallback } from "react";

import {
  Alert,
  Button,
  Container,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router";

import { PROJECTS } from "src/platform/constants/routes";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";
import debugLogger from "src/platform/utils/debugLogger";
import {
  getButtonStyles,
  getContainerStyles,
} from "src/shell/styles/themeUtils";

import type { Project } from "../../module/types/project.types";

import {
  BasicInfoSection,
  FinOpsTagsSection,
  ProjectBreadcrumbs,
  SubmitButton,
} from "./components";
import { useOrgTagSchema } from "./hooks/useOrgTagSchema";
import { slugify } from "./utils";

export default function CreateProjectPage() {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [name, setName] = useState("");
  const [aliasOverride, setAliasOverride] = useState("");
  const [aliasTouched, setAliasTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string>("");

  const { tagSchema, tagValues, teams, setTagValues } =
    useOrgTagSchema(orgAlias);

  const { call: createProject } = useRegistryApi<Project>(
    "project",
    "createProject",
  );

  const autoAlias = slugify(name);
  const effectiveAlias = aliasTouched ? aliasOverride : autoAlias;

  const requiredTags = tagSchema.filter((t) => t.required);
  const missingRequiredTags = requiredTags.filter(
    (t) => !tagValues[t.key]?.trim(),
  );
  const canSubmit =
    name.trim().length >= 3 &&
    effectiveAlias.length > 0 &&
    !isSubmitting &&
    missingRequiredTags.length === 0;

  const validateName = useCallback((value: string) => {
    if (!value.trim()) {
      setNameError("Project name is required");
      return false;
    }
    if (value.length < 3) {
      setNameError("Project name must be at least 3 characters");
      return false;
    }
    setNameError("");
    return true;
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!orgAlias || !canSubmit) return;
    if (!validateName(name)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const project = await createProject({
        body: {
          name: name.trim(),
          alias: effectiveAlias,
          description: description.trim() || undefined,
          tags: tagValues,
          teamId: teamId ? Number(teamId) : undefined,
        },
      });
      void navigate(PROJECTS.DETAILS.getPath(orgAlias, project.alias));
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Failed to create project";
      debugLogger.error("create-project", "Project creation failed", msg);
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Container sx={getContainerStyles(theme, "sm")}>
      <ProjectBreadcrumbs orgAlias={orgAlias} />

      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" fontWeight={700}>
              Create a project
            </Typography>
            <Button
              variant="outlined"
              component={RouterLink}
              to={orgAlias ? PROJECTS.LIST.getPath(orgAlias) : "/"}
              disabled={isSubmitting}
              sx={getButtonStyles(theme, "outlined")}
            >
              Cancel
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Projects group related systems within {orgAlias}.
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {submitError}
            </Alert>
          )}

          {/* Basic Information */}
          <BasicInfoSection
            name={name}
            setName={setName}
            aliasOverride={aliasOverride}
            setAliasOverride={setAliasOverride}
            aliasTouched={aliasTouched}
            setAliasTouched={setAliasTouched}
            description={description}
            setDescription={setDescription}
            nameError={nameError}
            validateName={validateName}
            isSubmitting={isSubmitting}
            effectiveAlias={effectiveAlias}
            teams={teams}
            teamId={teamId}
            setTeamId={setTeamId}
            orgAlias={orgAlias}
          />

          {/* FinOps Tags */}
          <FinOpsTagsSection
            tagSchema={tagSchema}
            tagValues={tagValues}
            setTagValues={setTagValues}
            isSubmitting={isSubmitting}
            requiredTags={requiredTags}
          />

          {/* Submit */}
          <SubmitButton canSubmit={canSubmit} isSubmitting={isSubmitting} />
        </Stack>
      </form>
    </Container>
  );
}
